import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { getApi } from 'views/services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import LocalHotelIcon from '@mui/icons-material/LocalHotel';
import ReceiptIcon from '@mui/icons-material/Receipt';

const SeparateFoodInvView = () => {
  const [invoiceData, setInvoiceData] = useState({});
  const [printSize, setPrintSize] = useState('A5');
  const hotel = JSON.parse(localStorage.getItem('hotelData') || '{}');
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        const response = await getApi(`api/separatefoodinvoice/viewspecificinvoice/${id}`);
        setInvoiceData(response?.data?.InvoiceData);
      } catch (error) {
        console.error(error);
      }
    };
    fetchInvoiceData();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handlePrintSizeChange = (event) => {
    setPrintSize(event.target.value);
  };

  const currentDate = new Date(invoiceData?.createdDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return (
    <Box
      sx={{
        padding: '20px',
        backgroundColor: '#f5f5f5',
        minHeight: { xs: '100vh', print: 'auto' }
      }}
    >
      {/* Header Controls */}
      <Box
        className="no-print"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{
            color: '#5a4b81',
            borderColor: '#5a4b81',
            '&:hover': { backgroundColor: '#f0edf5', borderColor: '#5a4b81' }
          }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="print-size-label">Paper Size</InputLabel>
            <Select
              labelId="print-size-label"
              value={printSize}
              label="Paper Size"
              onChange={handlePrintSizeChange}
            >
              <MenuItem value="A4">A4</MenuItem>
              <MenuItem value="A5">A5</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            sx={{
              backgroundColor: '#5a4b81',
              '&:hover': { backgroundColor: '#42385e' }
            }}
          >
            Print Invoice
          </Button>
        </Box>
      </Box>

      {/* Invoice Content */}
      <Box
        className="invoice-container"
        sx={{
          backgroundColor: 'white',
          padding: '30px',
          maxWidth: printSize === 'A4' ? '800px' : '600px',
          margin: '0 auto',
          boxShadow: '0px 5px 15px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0',
          borderRadius: '8px'
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocalHotelIcon sx={{ color: '#5a4b81', mr: 1, fontSize: '32px' }} />
              <Typography variant="h4" sx={{ color: '#5a4b81', fontWeight: 'bold' }}>
                {hotel?.name}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#666' }}>{hotel?.address}</Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>Tel: {hotel?.contact}</Typography>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
              <ReceiptIcon sx={{ color: '#5a4b81', mr: 1 }} />
              <Typography variant="h5" sx={{ color: '#5a4b81', fontWeight: 'bold' }}>
                INVOICE
              </Typography>
            </Box>
            <Typography variant="body2"><strong>Invoice #:</strong> {invoiceData?.invoiceNumber}</Typography>
            <Typography variant="body2"><strong>Date:</strong> {currentDate}</Typography>
            <Typography variant="body2"><strong>Payment:</strong> {invoiceData?.paymentMethod}</Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#5a4b81', borderWidth: '1px', mb: 2 }} />

        {/* Customer Details */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ color: '#5a4b81', fontWeight: 'bold', mb: 1 }}>
              BILL TO
            </Typography>
            <Card variant="outlined" sx={{ padding: '16px', backgroundColor: '#f9f9f9' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{invoiceData?.name}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }} fontSize="13px">{invoiceData?.address}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }} fontSize="13px">Phone: {invoiceData?.customerPhoneNumber}</Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ color: '#5a4b81', fontWeight: 'bold', mb: 1 }}>
              GST / TAX INFO
            </Typography>
            <Card variant="outlined" sx={{ padding: '16px', backgroundColor: '#f9f9f9' }}>
              <Typography variant="body2" fontSize="13px"><strong>GST Number:</strong> {invoiceData?.gstNumber || 'N/A'}</Typography>
            </Card>
          </Grid>

          {/* Invoice Items */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6" sx={{ color: '#5a4b81', fontWeight: 'bold', mb: 2 }}>
              INVOICE DETAILS
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f0edf5' }}>
                    <TableCell sx={{ fontWeight: 'bold', padding: '12px' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', padding: '12px' }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', padding: '12px' }}>Quantity</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', padding: '12px' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceData?.foodItems?.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell sx={{ padding: '10px', fontSize: "13px" }}><b>{item.name}</b></TableCell>
                      <TableCell sx={{ padding: '10px', fontSize: "13px" }}>{item.price}</TableCell>
                      <TableCell sx={{ padding: '10px', fontSize: "13px" }}>{item.quantity}</TableCell>
                      <TableCell align="right" sx={{ padding: '10px', fontSize: "13px" }}>Rs {item.price * item.quantity}</TableCell>
                    </TableRow>
                  ))}

                  {/* Discount / GST / Total */}
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontSize: "13px" }}><b>Discount</b></TableCell>
                    <TableCell align="right" sx={{ fontSize: "13px" }}>Rs {invoiceData?.discount || 0}</TableCell>
                  </TableRow>
                  {invoiceData?.haveGST && (
                    <TableRow>
                      <TableCell colSpan={3} align="right" sx={{ fontSize: "13px" }}><b>GST</b></TableCell>
                      <TableCell align="right" sx={{ fontSize: "13px" }}>Rs {invoiceData?.gstAmount}</TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ borderTop: '2px solid #ddd' }}>
                    <TableCell colSpan={3} align="right" sx={{ fontSize: "13px" }}><b>Total Amount</b></TableCell>
                    <TableCell align="right" sx={{ fontSize: "13px" }}>Rs {invoiceData?.totalAmount}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Footer */}
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 4 }}>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="body2" sx={{ borderTop: '1px solid #000', display: 'inline-block', paddingTop: '10px', minWidth: '150px' }}>
                Customer Signature
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="body2" sx={{ borderTop: '1px solid #000', display: 'inline-block', paddingTop: '10px', minWidth: '150px' }}>
                Authorized Signature
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            @page { size: ${printSize}; margin: 0; }
            body, html { margin: 0 !important; padding: 0 !important; height: auto !important; overflow: visible !important; }
            .no-print { display: none !important; }
            .invoice-container { box-shadow: none !important; border: none !important; margin: 0 auto !important; padding: 0 !important; }
            table { font-size: ${printSize === 'A5' ? '9px' : '11px'}; }
          }
        `}
      </style>
    </Box>
  );
};

export default SeparateFoodInvView;
